import type { NodeLayout } from './layout';
import type { Status } from './data';
import { iconSvg } from './icons';
import { STATUS_BUTTON_ICON } from './renderer';
import * as api from './api';

export interface EventPanelHandle {
  /**
   * Point the panel at a node (or pass null to show the page-wide event feed)
   * and load the matching timeline.
   */
  setNode: (id: string | null) => void;
  /** Re-render the current view (re-reads label/status, re-fetches the timeline). */
  refresh: () => void;
  /**
   * Prepend a single new event to the open list (no full reload) — if it belongs
   * to the current scope (page feed, or the connected node's timeline). Deduped.
   */
  addEvent: (e: api.ApiEvent) => void;
  /** Highlight the card of the event currently being played (null clears it). */
  markPlaying: (id: string | null) => void;
}

const STATUS_LABEL: Record<Status, string> = {
  'todo':        'Todo',
  'in-progress': 'In progress',
  'done':        'Done',
  'blocked':     'Blocked',
};

export function setupEventPanel(
  pageId:       string,
  layouts:      Map<string, NodeLayout>,
  onEventClick: (nodeId: string, status: Status | null) => void = () => {},
): EventPanelHandle {
  const body = document.getElementById('event-body') as HTMLDivElement;

  let nodeId:    string | null = null;
  let playingId: string | null = null; // event currently being played by the queue
  // Guards against a stale fetchEvents resolving after the user switched nodes.
  let gen = 0;

  const statusDot = (status: Status, label: string): string =>
    `<span class="ep-status-dot" style="color:var(--status-${status})">` +
    `${iconSvg(STATUS_BUTTON_ICON[status], 14)}</span>${escapeHtml(label)}`;

  // Read-only node header + timeline (events are sent via the API, not this panel).
  const nodeViewHtml = (layout: NodeLayout): string =>
    `<div class="ep-node">` +
      `<span class="ep-status-dot" style="color:var(--status-${layout.status})">` +
        `${iconSvg(STATUS_BUTTON_ICON[layout.status], 16)}</span>` +
      `<span class="ep-node-label">${escapeHtml(layout.label)}</span>` +
    `</div>` +
    `<ul id="event-list"></ul>`;

  // `showNode` prefixes each row with its node's label — used by the page feed,
  // where events come from many different nodes.
  const eventHtml = (e: api.ApiEvent, showNode = false): string => {
    const head = e.status
      ? `<span class="ep-event-status">${statusDot(e.status, STATUS_LABEL[e.status])}</span>`
      : `<span class="ep-event-status ep-info">Info</span>`;
    const node = showNode
      ? `<div class="ep-event-node">${escapeHtml(layouts.get(e.node_id)?.label ?? '(node)')}</div>`
      : '';
    // Rename line keeps the previous name alongside the new one.
    const rename = e.label
      ? `<p class="ep-event-rename">` +
          `<span class="ep-old-name">${escapeHtml(e.prev_label ?? '')}</span>` +
          ` → <span class="ep-new-name">${escapeHtml(e.label)}</span></p>`
      : '';
    const note   = e.note   ? `<p class="ep-event-note">${escapeHtml(e.note)}</p>` : '';
    const author = e.author ? `<span class="ep-event-author">— ${escapeHtml(e.author)}</span>` : '';
    return (
      `<li class="ep-event${e.id === playingId ? ' ep-event-playing' : ''}" ` +
        `data-event-id="${e.id}" data-node-id="${e.node_id}" data-status="${e.status ?? ''}">` +
        node +
        `<div class="ep-event-head">${head}<time>${escapeHtml(formatTime(e.created_at))}</time></div>` +
        rename + note + author +
      `</li>`
    );
  };

  const renderTimeline = (events: api.ApiEvent[], showNode = false): void => {
    const list = document.getElementById('event-list');
    if (!list) return;
    list.innerHTML = events.length === 0
      ? `<li class="ep-event-empty">No events yet.</li>`
      : events.map(e => eventHtml(e, showNode)).join('');
  };

  // No node connected → show every event on the page, newest first, read-only.
  const renderFeed = (): void => {
    body.innerHTML =
      `<div class="ep-node ep-feed-head"><span class="ep-node-label">All page events</span></div>` +
      `<ul id="event-list"></ul>`;

    const myGen = ++gen;
    renderTimeline([]); // placeholder cleared by the fetch below
    api.fetchAllPageEvents(pageId)
      .then(events => { if (myGen === gen) renderTimeline(events, true); })
      .catch(err => {
        console.warn('[eventPanel] fetchAllPageEvents failed', err);
        if (myGen === gen) renderTimeline([], true);
      });
  };

  const render = (): void => {
    const layout = nodeId !== null ? layouts.get(nodeId) : undefined;
    if (!layout) {
      renderFeed();
      return;
    }

    body.innerHTML = nodeViewHtml(layout);
    const myGen = ++gen;
    renderTimeline([]); // placeholder cleared by the fetch below
    api.fetchEvents(layout.id)
      .then(events => { if (myGen === gen) renderTimeline(events); })
      .catch(err => {
        console.warn('[eventPanel] fetchEvents failed', err);
        if (myGen === gen) renderTimeline([]);
      });
  };

  const isOpen = (): boolean => document.body.classList.contains('events-open');

  // Clicking an event card replays its animation on the concerned node.
  // Delegated on `body` (stable) so it survives timeline re-renders.
  body.addEventListener('click', (ev) => {
    const li = (ev.target as HTMLElement).closest('.ep-event') as HTMLElement | null;
    if (!li || !li.dataset.nodeId) return;
    onEventClick(li.dataset.nodeId, (li.dataset.status || null) as Status | null);
  });

  // Feed mode = no node connected (null id, or the node isn't on this page).
  const inFeedMode = (): boolean => nodeId === null || !layouts.get(nodeId);

  const addEvent = (e: api.ApiEvent): void => {
    if (!isOpen()) return; // closed → the list re-fetches fresh when reopened
    const feed = inFeedMode();
    if (!feed && e.node_id !== nodeId) return; // node mode: only this node's events
    const list = document.getElementById('event-list');
    if (!list || list.querySelector(`[data-event-id="${e.id}"]`)) return; // dedupe
    const empty = list.querySelector('.ep-event-empty');
    if (empty) empty.remove();
    list.insertAdjacentHTML('afterbegin', eventHtml(e, feed));
  };

  const markPlaying = (id: string | null): void => {
    playingId = id;
    const list = document.getElementById('event-list');
    if (!list) return;
    list.querySelectorAll('.ep-event-playing').forEach(el => el.classList.remove('ep-event-playing'));
    if (id) list.querySelector(`[data-event-id="${id}"]`)?.classList.add('ep-event-playing');
  };

  if (isOpen()) render(); // otherwise the toggle renders on open

  return {
    // Track the selection always, but only render/fetch while the panel is
    // visible — the toggle calls setNode() again on open, so it loads then.
    setNode: (id: string | null) => { nodeId = id; if (isOpen()) render(); },
    refresh: () => { if (isOpen()) render(); },
    addEvent,
    markPlaying,
  };
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
